<script setup lang="ts">
import { Navigation, Phone, ShoppingBag, TriangleAlert } from 'lucide-vue-next'
import type { Component } from 'vue'

import PanelGroup from '../PanelGroup.vue'
import {
  BRAND_APP_STORE_ACTIONS,
  missingStoreActions,
  type BrandAppShowcaseStore,
  type BrandAppStoreActionId
} from '../../lib/brandAppContract'
import useBrandAppConfig from '../../composables/useBrandAppConfig'

const props = defineProps<{
  stores: BrandAppShowcaseStore[]
}>()

const { t } = useI18n()
const { order, isStoreHidden, isStoreActionEnabled, setStoreActionEnabled } = useBrandAppConfig()

const ICONS: Record<BrandAppStoreActionId, Component> = {
  directions: Navigation,
  order: ShoppingBag,
  call: Phone
}

const visibleStores = computed(() => props.stores.filter((store) => !isStoreHidden(store.id)))

const missingCount = (id: BrandAppStoreActionId) =>
  visibleStores.value.filter((store) => missingStoreActions(store, [id], order.value).length > 0).length

const subtitle = (id: BrandAppStoreActionId) => {
  const action = BRAND_APP_STORE_ACTIONS.find((item) => item.id === id)
  if (!action || !isStoreActionEnabled(id)) return { text: action?.source ?? '', warning: false }
  const count = missingCount(id)
  if (count === 0) return { text: action.source, warning: false }

  return {
    text: t(
      'pages.loyaltySupports.webapp.editor.stores.actionsPanel.missing',
      { count, missing: action.missing },
      count
    ),
    warning: true
  }
}
</script>

<template>
  <PanelGroup>
    <p class="text-muted-foreground text-xs">
      {{ t('pages.loyaltySupports.webapp.editor.stores.actionsPanel.hint') }}
    </p>
    <div class="flex flex-col gap-2">
      <label
        v-for="(action, index) in BRAND_APP_STORE_ACTIONS"
        :key="action.id"
        :for="`brandApp.stores.actions.${action.id}`"
        class="border-border hover:border-foreground-disabled motion-safe:animate-card-in flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors"
        :class="isStoreActionEnabled(action.id) ? 'bg-background shadow-surface' : 'bg-muted/50'"
        :style="{ animationDelay: `${Math.min(index, 5) * 40}ms` }"
      >
        <span
          class="flex size-10 shrink-0 items-center justify-center rounded-xl"
          :class="isStoreActionEnabled(action.id) ? 'bg-muted' : 'bg-background'"
        >
          <component
            :is="ICONS[action.id]"
            class="size-4.5"
            :class="isStoreActionEnabled(action.id) ? 'text-primary' : 'text-foreground-disabled'"
          />
        </span>
        <span class="flex min-w-0 flex-1 flex-col">
          <span
            class="truncate text-sm font-medium"
            :class="isStoreActionEnabled(action.id) ? 'text-foreground' : 'text-foreground-secondary'"
          >
            {{ action.label }}
          </span>
          <span
            class="flex items-center gap-1 truncate text-xs"
            :class="subtitle(action.id).warning ? 'text-amber-700' : 'text-muted-foreground'"
          >
            <TriangleAlert v-if="subtitle(action.id).warning" class="size-3 shrink-0" />
            {{ subtitle(action.id).text }}
          </span>
        </span>
        <Switch
          :id="`brandApp.stores.actions.${action.id}`"
          :model-value="isStoreActionEnabled(action.id)"
          @update:model-value="setStoreActionEnabled(action.id, $event)"
        />
      </label>
    </div>
  </PanelGroup>
</template>
